/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React from 'react';
import clsx from 'clsx';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import FileInput from 'carbon-react/lib/components/file-input';
import Form from 'carbon-react/lib/components/form';
import Icon from 'carbon-react/lib/components/icon';
import Message from 'carbon-react/lib/components/message';
import Typography from 'carbon-react/lib/components/typography';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { ExcelEntityConfig } from '@futrli/views/ExcelFilesView';

import { StepType as StepTypeForecast } from '../hooks/use-upload-forecast';
import { StepType as StepTypeOrg } from '../hooks/use-upload-organisation';
import { UpdateHistory } from '../update/update-history';
import { UploadEntity } from './upload-entity';
import { useFileUploadStatus } from './use-file-upload-status';
import * as styles from './upload-modal.scss';

interface Props {
  children: JSX.Element;
  acceptedFileExtensions?: string;
  onCancel: () => void;
  isInProgress: boolean;
  uploadSuccess: boolean;
  uploadFailed: boolean;
  file?: File;
  errorMessage: string;
  warningMessage?: string;
  step: StepTypeOrg | StepTypeForecast;
  entity: UploadEntity;
  onFinish: () => Promise<void>;
  onCreate?: () => void;
  onDrop: (acceptedFiles: File[]) => void;
  isUpdateDrawer?: boolean;
  workspaceId?: string;
  organisationId?: string;
  historyConfig?: ExcelEntityConfig;
  disableCreate?: boolean;
  width?: number;
}

export const ExcelUploadDrawerForm = ({
  children,
  acceptedFileExtensions = '.xls,.xlsx',
  onCancel,
  isInProgress,
  uploadSuccess,
  uploadFailed,
  file,
  errorMessage,
  warningMessage,
  step,
  entity,
  onFinish,
  onCreate,
  onDrop,
  isUpdateDrawer,
  workspaceId,
  organisationId,
  historyConfig,
  disableCreate,
  width
}: Props) => {
  const intl = useIntl();

  const handleFileChange = (files: FileList) => {
    onDrop(Array.from(files));
  };

  const { uploadStatus } = useFileUploadStatus({
    step,
    entity,
    isInProgress,
    uploadSuccess,
    uploadFailed,
    file
  });

  return (
    <Form
      rightSideButtons={
        <>
          {onCreate && (
            <Button buttonType="primary" onClick={onCreate} disabled={isInProgress || disableCreate}>
              <FormattedMessage id="common.create" defaultMessage="Create" />
            </Button>
          )}
          <Button onClick={uploadSuccess ? onFinish : onCancel} disabled={isInProgress}>
            {uploadSuccess ? (
              <FormattedMessage id="common.finish" defaultMessage="Finish" />
            ) : (
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            )}
          </Button>
        </>
      }
      stickyFooter
      buttonAlignment="right"
      footerPadding={{ p: 2 }}
      onSubmit={e => e.preventDefault()}
    >
      {uploadSuccess ? (
        <Box display="flex" flexDirection="column" height="100%" alignItems="center" justifyContent="center">
          <Box>
            <Typography variant="p" fontWeight="500" textAlign="center" mb={0}>
              <Icon
                type="tick_thick"
                bgSize="extra-large"
                bgShape="circle"
                bg="--colorsActionMajor500"
                color="--colorsUtilityYang100"
                fontSize="large"
                mb={2}
              />
            </Typography>
            <Typography variant="p" fontWeight="500" textAlign="center">
              <FormattedMessage id="common.data_updated_successfully" defaultMessage="Data updated successfully" />
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box width={width}>
          {errorMessage && (
            <Message open variant="error" mb={3}>
              {errorMessage}
            </Message>
          )}
          {warningMessage && (
            <Message open variant="warning" mb={3}>
              {warningMessage}
            </Message>
          )}
          {children}
          <div className={clsx(styles.fileInputWrapper, styles.hideCancelAction)}>
            <FileInput
              buttonText={intl.formatMessage({ id: 'common.select_file', defaultMessage: 'Select file' })}
              dragAndDropText={intl.formatMessage({
                id: 'common.or_drag_and_drop_your_file',
                defaultMessage: 'or drag and drop your file'
              })}
              accept={acceptedFileExtensions}
              onChange={handleFileChange}
              maxWidth="100%"
              uploadStatus={uploadStatus}
              minHeight="93px"
            />
          </div>
          {isUpdateDrawer && workspaceId && organisationId && historyConfig && (
            <UpdateHistory workspaceId={workspaceId} organisationId={organisationId} config={historyConfig} />
          )}
        </Box>
      )}
    </Form>
  );
};
